import { z } from 'zod';
// Chat context descriptor — spec e85da0ee.
//
// WHAT THE FOUNDER IS LOOKING AT, as an address rather than as prose.
//
// THE CLIENT SENDS THIS AND NOTHING ELSE. It never sends the facts. Two
// reasons, and the first is the one that matters:
//
//   1. Client-sent context is injection into an agent that can propose spend.
//      Mag repeats what she is told; a crafted request would put words in her
//      mouth and the founder would read them as hers.
//   2. An id has to be access-checked against the brand server-side anyway —
//      the project rule that sibling read and write endpoints enforce the same
//      authorization applies here, because resolving a descriptor IS a read.
//
// The server owns every fact, which also means the resolver is the only place
// that has to be right.
/**
 * The surfaces a conversation can be about.
 *
 * The full vocabulary ships from day one even though only `campaign` has a
 * resolver branch, so adding the next surface is a branch in one function
 * rather than a migration plus a schema change plus a client change. A kind
 * with no branch resolves to nothing and the founder gets today's generic
 * greeting — which is the correct answer, not a degraded one: a context block
 * only earns its place when Mag has something real to say about that surface.
 */
export const chatContextKindSchema = z.enum([
    /** A campaign detail page. Carries an id. */
    'campaign',
    /** An insight on a campaign. Today this is the insight-chat drawer's own
     *  surface; the vocabulary admits it so that drawer can migrate onto this
     *  path later (spec residue RES-2). */
    'campaign_insight',
    /** A content slot. Same note as campaign_insight — slot chat migrates later. */
    'content_slot',
    /** A brand-workbench section. The id is the section, not a uuid. */
    'brand_workbench',
    /** Brand-scoped surfaces that need no id at all. */
    'analytics',
    'calendar',
    'channels',
]);
/** Kinds whose id is a uuid pointing at a row the server must access-check.
 *  The others are either id-less or carry a non-uuid section name. */
export const UUID_SCOPED_CHAT_CONTEXT_KINDS = new Set([
    'campaign',
    'campaign_insight',
    'content_slot',
]);
export const chatContextDescriptorSchema = z.object({
    kind: chatContextKindSchema,
    /** The thing being looked at, when the kind names one. Absent for the
     *  brand-scoped surfaces. Never trusted: the resolver checks it belongs to
     *  the brand before reading anything. */
    id: z.string().min(1).max(120).optional(),
});
/** Render a descriptor for storage/comparison — the thread-addressing key.
 *  Stored split across two columns rather than as this string so the lookup
 *  can be a plain btree index; this exists for logs and equality checks. */
export function chatContextKey(d) {
    return d.id ? `${d.kind}:${d.id}` : d.kind;
}
//# sourceMappingURL=chat-context.js.map